<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAudiencesStore } from 'dashboard/stores/audiences';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import AudienceFormDialog from './AudienceFormDialog.vue';

const audienceId = defineModel({ type: [String, Number], default: '' });

const { t } = useI18n();
const audiencesStore = useAudiencesStore();

// A stale audience (referenced label/pipeline/stage since deleted) is
// rejected by the backend on campaign creation (Campaign#audience_record_must_not_be_stale),
// so it's left out here rather than offered as a dead-end choice.
const activeAudiences = computed(() =>
  audiencesStore.getAudiencesList.filter(audience => !audience.stale)
);

const hasHiddenStaleAudiences = computed(
  () => activeAudiences.value.length < audiencesStore.getAudiencesList.length
);

const options = computed(() =>
  activeAudiences.value.map(audience => ({
    value: audience.id,
    label: audience.name,
  }))
);

onMounted(() => {
  if (!audiencesStore.getAudiencesList.length) audiencesStore.get();
});

const formDialogRef = ref(null);

const openCreateDialog = () => {
  formDialogRef.value?.dialogRef.open();
};

const handleSave = async ({ name, criteria }) => {
  try {
    const audience = await audiencesStore.create({ name, criteria });
    audienceId.value = audience.id;
    useAlert(t('AUDIENCES.FORM.SAVE_SUCCESS'));
    formDialogRef.value?.dialogRef.close();
  } catch (error) {
    useAlert(error.message || t('AUDIENCES.FORM.SAVE_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-1">
    <ComboBox
      v-model="audienceId"
      :options="options"
      :placeholder="t('AUDIENCES.PICKER.PLACEHOLDER')"
    />
    <span v-if="hasHiddenStaleAudiences" class="text-xs text-n-amber-11">
      {{ t('AUDIENCES.PICKER.STALE_HIDDEN_NOTICE') }}
    </span>
    <button
      type="button"
      class="self-start text-xs text-n-slate-12 hover:underline"
      @click="openCreateDialog"
    >
      {{ t('AUDIENCES.PICKER.CREATE_ACTION') }}
    </button>
    <AudienceFormDialog ref="formDialogRef" @save="handleSave" />
  </div>
</template>
